import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../lib/supabase';

const { FiArrowLeft, FiUser, FiMail, FiShield, FiHelpCircle, FiMessageCircle, FiBookOpen, FiLock, FiLoader, FiCheckCircle, FiAlertTriangle } = FiIcons;

const Profile = ({ onNavigate, user }) => {
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Real user data states
  const [userQuestions, setUserQuestions] = useState([]);
  const [userLearnings, setUserLearnings] = useState([]);
  const [userStats, setUserStats] = useState({
    questionsCount: 0,
    learningsCount: 0,
    answersCount: 0
  });

  useEffect(() => {
    if (user) {
      fetchUserData();
    }
  }, [user]);

  const fetchUserData = async () => {
    try {
      setDataLoading(true);
      
      const userIdentifier = user.user_metadata?.full_name || user.email?.split('@')[0] || 'User';

      // Fetch user's questions
      const { data: questionsData, error: questionsError } = await supabase
        .from('questions_kb2024')
        .select('id, title, category, status, created_at')
        .eq('asked_by', userIdentifier)
        .order('created_at', { ascending: false })
        .limit(10);

      if (questionsError) throw questionsError;

      // Fetch user's learnings
      const { data: learningsData, error: learningsError } = await supabase
        .from('learnings_kb2024')
        .select('id, title, category, created_at')
        .eq('shared_by', userIdentifier)
        .order('created_at', { ascending: false })
        .limit(10);

      if (learningsError) throw learningsError;

      // Set the data
      setUserQuestions(questionsData || []);
      setUserLearnings(learningsData || []);
      
      // Update stats
      setUserStats({
        questionsCount: questionsData?.length || 0,
        learningsCount: learningsData?.length || 0,
        answersCount: 0
      });

    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;

      setMessage({
        type: 'success',
        text: 'Password reset link sent to your email. Please check your inbox.'
      });
    } catch (error) {
      console.error('Password reset error:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Failed to send reset email. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'bg-blue-100 text-blue-800';
      case 'Answered': return 'bg-green-100 text-green-800';
      case 'In Progress': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Certification': 'bg-blue-100 text-blue-800',
      'QDMS': 'bg-green-100 text-green-800',
      'QHSE': 'bg-red-100 text-red-800',
      'Forms and Checklists': 'bg-purple-100 text-purple-800',
      'Procurement': 'bg-orange-100 text-orange-800',
      'Maintenance': 'bg-yellow-100 text-yellow-800',
      'General': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getRoleColor = (role) => {
    const colors = {
      'Super User': 'bg-purple-100 text-purple-800',
      'Maersk Team': 'bg-blue-100 text-blue-800',
      'Mariapps SME': 'bg-green-100 text-green-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getUserRole = () => {
    return user?.user_metadata?.user_role || 'User';
  };

  const getUserDomain = () => {
    return user?.email?.split('@')[1] || '';
  };

  if (dataLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiLoader} className="w-8 h-8 text-blue-600 animate-spin" />
            <span className="text-lg text-gray-600">Loading profile...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => onNavigate('dashboard')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors duration-200"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
          Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">My Profile</h1>
        <p className="text-gray-600">View your activity and contributions</p>
      </motion.div>

      {/* User Details */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 mb-8"
      >
        <div className="flex items-start gap-6">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
            <SafeIcon icon={FiUser} className="w-10 h-10 text-blue-600" />
          </div>
          <div className="flex-1">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User'}
                </h2>
                <div className="flex items-center gap-4 text-gray-600 mb-3">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiMail} className="w-4 h-4" />
                    <span>{user?.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiShield} className="w-4 h-4" />
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(getUserRole())}`}>
                      {getUserRole()}
                    </span>
                  </div>
                </div>
                
                {/* Domain Information */}
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                  <span>Domain: {getUserDomain()}</span>
                </div>
              </div>
              <button
                onClick={() => setShowPasswordReset(!showPasswordReset)}
                className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                <SafeIcon icon={FiLock} className="w-4 h-4" />
                Reset Password
              </button>
            </div>

            {/* Password Reset Section */}
            {showPasswordReset && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6 p-4 bg-gray-50 rounded-lg border"
              >
                <h3 className="font-medium text-gray-900 mb-3">Reset Password</h3>
                {message.text && (
                  <div className={`mb-4 p-3 rounded-lg border text-sm ${
                    message.type === 'success'
                      ? 'bg-green-50 border-green-200 text-green-700'
                      : 'bg-red-50 border-red-200 text-red-700'
                  }`}>
                    <div className="flex items-center gap-2">
                      <SafeIcon 
                        icon={message.type === 'success' ? FiCheckCircle : FiAlertTriangle} 
                        className="w-4 h-4" 
                      />
                      <span>{message.text}</span>
                    </div>
                  </div>
                )}
                <p className="text-sm text-gray-600 mb-4">
                  Click the button below to receive a password reset link at your email address.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handlePasswordReset}
                    disabled={loading}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiMail} className="w-4 h-4" />
                        Send Reset Link
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowPasswordReset(false)}
                    className="py-2 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            )}

            <div className="grid grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{userStats.questionsCount}</div>
                <div className="text-sm text-gray-600">Questions Asked</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{userStats.answersCount}</div>
                <div className="text-sm text-gray-600">Answers Given</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{userStats.learningsCount}</div>
                <div className="text-sm text-gray-600">Learnings Shared</div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Activity Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Questions Asked */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100"
        >
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiHelpCircle} className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">My Questions</h3>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {userQuestions.length > 0 ? (
                userQuestions.map((question, index) => (
                  <motion.div
                    key={question.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="p-4 border border-gray-100 rounded-lg hover:border-gray-200 transition-colors duration-200 cursor-pointer"
                    onClick={() => onNavigate('question-details', question.id)}
                  >
                    <h4 className="font-medium text-gray-900 mb-2 text-sm">{question.title}</h4>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(question.category)}`}>
                        {question.category}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(question.status)}`}>
                        {question.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">{formatDate(question.created_at)}</div>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-8">
                  <SafeIcon icon={FiHelpCircle} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm mb-3">No questions asked yet</p>
                  <button
                    onClick={() => onNavigate('question-form')}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Ask your first question
                  </button>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Answers Given - Placeholder for future implementation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100"
        >
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiMessageCircle} className="w-5 h-5 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900">My Answers</h3>
            </div>
          </div>
          <div className="p-6">
            <div className="text-center py-8">
              <SafeIcon icon={FiMessageCircle} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm mb-3">No answers given yet</p>
              <button
                onClick={() => onNavigate('questions')}
                className="text-green-600 hover:text-green-700 text-sm font-medium"
              >
                Browse questions to answer
              </button>
            </div>
          </div>
        </motion.div>

        {/* Learnings Shared */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100"
        >
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiBookOpen} className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-semibold text-gray-900">My Learnings</h3>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {userLearnings.length > 0 ? (
                userLearnings.map((learning, index) => (
                  <motion.div
                    key={learning.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="p-4 border border-gray-100 rounded-lg hover:border-gray-200 transition-colors duration-200 cursor-pointer"
                    onClick={() => onNavigate('learning-details', learning.id)}
                  >
                    <h4 className="font-medium text-gray-900 mb-2 text-sm">{learning.title}</h4>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(learning.category)}`}>
                        {learning.category}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">{formatDate(learning.created_at)}</div>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-8">
                  <SafeIcon icon={FiBookOpen} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm mb-3">No learnings shared yet</p>
                  <button
                    onClick={() => onNavigate('learning-form')}
                    className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                  >
                    Share your first learning
                  </button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Profile;